import { useEffect, useRef, useState } from 'react';
import { AgentTranscriptEntry } from '../../types/agentHarness';
import { humanizeToolName, uncapitalize } from '../utils/OscarLabels';
import { OSCAR_RETRY_COPY } from '../constants/AIAgentConstants';

/**
 * What the agent is doing right now, and for how long.
 *
 * Labels are lower case: this is ambient status sitting under sentence-case
 * prose, and capitalising it makes it compete with the answer.
 *
 * The label is derived from the stream wherever the stream actually says
 * something — a named tool, an answer starting, a question waiting on the user —
 * and falls back to a steady word plus elapsed time otherwise. A purely
 * time-driven sequence of phrases would be theatre; this reports real state and
 * uses time only to admit when something is slow.
 *
 * It does **not** narrate the model's reasoning. That was tried, and two things
 * were wrong with it. What can be shown is only ever a fragment of a thought
 * that is immediately replaced, which flickers however much it is smoothed; and
 * that fragment appears nowhere once the turn settles, so the user is shown
 * something the finished conversation does not contain. The reasoning has a
 * home already — the collapsed activity line above the answer, where it
 * is whole and stays put.
 */
const SLOW_AFTER_SECONDS = 8;

/**
 * How long the answer text can sit unchanged before we stop treating it as the
 * progress indicator.
 *
 * "The text is the progress" holds only while the text is actually growing. A
 * model that has finished its sentence and is now composing a tool call — the
 * whole body of a CSV, say — is still working hard, but everything it produces
 * goes into arguments the stream does not reveal until `tool_start` fires with
 * the call complete. That gap could run for many seconds with the indicator
 * hidden, which is exactly the "nothing is happening" it exists to prevent.
 */
const TEXT_STALE_AFTER_MS = 1500;
const VERY_SLOW_AFTER_SECONDS = 25;

export interface OscarTurnStatus {
	/** Null while idle — the caller renders nothing. */
	label: string | null;
	/** Whole seconds since the turn began. */
	seconds: number;
	/** The turn is parked on a write the user has to approve. */
	awaitingApproval: boolean;
}

/** The assistant message currently being written, if there is one. */
const openAssistantText = (entries: AgentTranscriptEntry[]): string => {
	for (let index = entries.length - 1; index >= 0; index -= 1) {
		const entry = entries[index];
		if (entry.kind === 'assistant') {
			// Superseded text was abandoned for a retry; it is not being written.
			return entry.done || entry.superseded ? '' : entry.text;
		}
		if (entry.kind === 'user') {
			return '';
		}
	}
	return '';
};

const labelForEntries = (
	entries: AgentTranscriptEntry[],
	seconds: number,
	textIsStale: boolean
): string => {
	// Walk back to the most recent thing that says what is happening.
	for (let index = entries.length - 1; index >= 0; index -= 1) {
		const entry = entries[index];
		if ((entry.kind === 'assistant' || entry.kind === 'tool') && entry.superseded) {
			// The newest thing is an abandoned attempt: the retry has not shown
			// anything yet. Say it is starting over, not what the old attempt did.
			return OSCAR_RETRY_COPY.status;
		}
		if (entry.kind === 'tool' && !entry.done) {
			return `running ${uncapitalize(humanizeToolName(entry.name))}`;
		}
		if (entry.kind === 'assistant' && !entry.done) {
			// Text on screen is its own progress — but only while it is still
			// arriving. Once it has gone quiet the model has moved on to something
			// invisible, and hiding the indicator makes that look like a stall.
			if (entry.text && !textIsStale) {
				return '';
			}
			// "working" rather than "thinking" once an answer is already on screen:
			// the model has done its thinking and is now building something.
			return entry.text ? 'working' : 'thinking';
		}
		if (entry.kind === 'assistant' && entry.done) {
			// A finished message with the turn still running: the agent has written
			// something and is deciding what to do next. Stop walking — an older
			// tool call is not what is happening now — and let the elapsed-time
			// fallback below speak.
			break;
		}
		if (entry.kind === 'tool' && entry.done) {
			return 'reading the results';
		}
		if (entry.kind === 'user') {
			break;
		}
	}
	if (seconds >= VERY_SLOW_AFTER_SECONDS) {
		return 'still working — this one is taking a while';
	}
	if (seconds >= SLOW_AFTER_SECONDS) {
		return 'still thinking';
	}
	return 'thinking';
};

/**
 * @param startedAt `useAgentChat().turnStartedAt` — when the turn began, in
 * epoch ms, or 0 while idle. Passed in rather than stamped here so the clock
 * measures the **turn**: the hook re-runs constantly as entries stream, and a
 * locally-stamped start would reset on any interruption in `busy`.
 */
export const useOscarTurnStatus = (
	busy: boolean,
	entries: AgentTranscriptEntry[],
	startedAt: number
): OscarTurnStatus => {
	const [seconds, setSeconds] = useState(0);

	// Tracked across renders rather than in an effect because the answer is
	// needed in the same render that reads it. Writing a cache during render is
	// idempotent — the comparison simply re-runs.
	const streamedText = openAssistantText(entries);
	const lastTextRef = useRef('');
	const textChangedAtRef = useRef(0);
	if (streamedText !== lastTextRef.current) {
		lastTextRef.current = streamedText;
		textChangedAtRef.current = Date.now();
	}
	// The clock below ticks every second while a turn is live, so this is
	// re-evaluated without needing a timer of its own.
	const textIsStale = !!streamedText && Date.now() - textChangedAtRef.current > TEXT_STALE_AFTER_MS;

	// An approval is the one wait that is not the agent's, so the clock stops
	// while one is pending and the wait is left out of the turn's time — counting
	// the reader's own thinking as work would inflate the settled "worked for".
	// Tracked during render for the same reason as the text above: the approval
	// resolving is itself a render, so the pause closes in the render that ends it.
	const awaitingApproval =
		busy && entries.some((entry) => entry.kind === 'approval' && !entry.resolved);
	const pauseTurnRef = useRef(0);
	const pausedMsRef = useRef(0);
	const pauseStartedAtRef = useRef(0);
	if (pauseTurnRef.current !== startedAt) {
		pauseTurnRef.current = startedAt;
		pausedMsRef.current = 0;
		pauseStartedAtRef.current = 0;
	}
	if (awaitingApproval && !pauseStartedAtRef.current) {
		pauseStartedAtRef.current = Date.now();
	} else if (!awaitingApproval && pauseStartedAtRef.current) {
		pausedMsRef.current += Date.now() - pauseStartedAtRef.current;
		pauseStartedAtRef.current = 0;
	}

	// A ticking clock is exactly the "synchronise with something outside React"
	// case an effect is for. It runs only while a turn is in flight, and only
	// re-subscribes at a turn boundary — `startedAt` is stable within a turn.
	useEffect(() => {
		if (!busy || !startedAt) {
			setSeconds(0);
			return undefined;
		}
		const tick = () => {
			const now = Date.now();
			// Includes a pause still in progress, so the count freezes during it
			// rather than jumping by the whole wait when it ends.
			const pausedMs =
				pausedMsRef.current + (pauseStartedAtRef.current ? now - pauseStartedAtRef.current : 0);
			setSeconds(Math.max(0, Math.floor((now - startedAt - pausedMs) / 1000)));
		};
		// Catches up immediately: a turn resumed from `hello` may already be
		// several seconds old, and waiting a beat to show that reads as a stall.
		tick();
		const timer = window.setInterval(tick, 1000);
		return () => window.clearInterval(timer);
	}, [busy, startedAt]);

	if (!busy) {
		return { label: null, seconds: 0, awaitingApproval: false };
	}
	// Naming the approval wait stops the row claiming to run a tool while it is
	// really sitting on a question, and the elapsed time is hidden because
	// counting the user's own thinking and then calling it slow would be absurd.
	if (awaitingApproval) {
		return { label: 'waiting for your approval', seconds: 0, awaitingApproval: true };
	}

	const label = labelForEntries(entries, seconds, textIsStale);
	return { label: label || null, seconds, awaitingApproval: false };
};

export default useOscarTurnStatus;
